"use client"

import { createPortal } from "react-dom"
import { X } from "lucide-react"
import ReviewForm from "./ReviewForm"
import { formatEventDate } from "~/lib/event-dates"

export type PendingReviewLike = {
  event_id: string
  event: {
    title: string
    start_datetime?: string | null
    end_datetime?: string | null
  }
}

// Shared "write a review" dialog. Extracted from the old /activity page so
// the same modal can be launched from Home and My Bookings.
export function ReviewModal({
  pending,
  accessToken,
  onClose,
  onSubmitted,
}: {
  pending: PendingReviewLike
  accessToken: string
  onClose: () => void
  onSubmitted: () => void
}) {
  // Portaled to document.body: Home wraps its content in its own
  // position:fixed div, which creates a stacking context that traps a
  // nested z-index below AppHeader's sibling z-50 context — raising this
  // dialog's own z-index alone can't escape that (see #887). z-[100] matches
  // the other full-screen modals now that it's portaled alongside them.
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral hover:text-neutral-emphasis"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <ReviewForm
          eventId={pending.event_id}
          eventTitle={pending.event.title}
          eventDate={
            pending.event.start_datetime
              ? formatEventDate(pending.event, "en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })
              : ""
          }
          accessToken={accessToken}
          onSubmit={onSubmitted}
          onCancel={onClose}
        />
      </div>
    </div>,
    document.body,
  )
}

export default ReviewModal
